import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { formatViews } from "@/utils/media";

interface MovieCardProps {
  movie: Movie;
  showDescription?: boolean;
  rank?: number;
}

export default function MovieCard({ movie, showDescription = false, rank }: MovieCardProps) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-100 border border-background-200 hover:border-primary-400 transition-colors cursor-pointer"
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
        <img
          src={movie.cover}
          alt={`${movie.title} 海报`}
          title={`${movie.title} ${movie.type}`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/85 via-transparent to-transparent"></div>

        {typeof rank === "number" && (
          <span className="absolute top-2 left-2 w-7 h-7 rounded-md bg-accent-500 text-background-50 font-label text-base flex items-center justify-center">
            {rank}
          </span>
        )}

        <span className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-background-950/70 backdrop-blur-sm text-[11px] font-medium text-foreground-900">
          {movie.type}
        </span>

        <span className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-accent-500/95 text-background-50 text-xs font-bold">
          <i className="ri-star-fill text-[11px]"></i>
          {movie.score.toFixed(1)}
        </span>

        <span className="absolute bottom-2 right-2 flex items-center gap-1 text-[11px] text-foreground-800">
          <i className="ri-play-circle-line"></i>
          {formatViews(movie.views)}
        </span>

        <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center text-background-50">
            <i className="ri-play-fill text-2xl"></i>
          </span>
        </span>
      </div>

      <div className="flex flex-col gap-1.5 p-3">
        <h3 className="font-heading font-bold text-sm text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
          {movie.title}
        </h3>

        {showDescription && (
          <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">
            {movie.description}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 text-[11px] text-foreground-500">
          <span className="truncate">
            {movie.region} · {movie.year}
          </span>
          <span className="shrink-0">{movie.updateTime.slice(5)}</span>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-0.5">
          {movie.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}